import { useState, useReducer, useEffect, useCallback, useMemo } from 'react';
import {
    GridActionsCellItem,
    GridColDef,
    GridRowId,
    useGridApiRef,
    GridApi,
    GridRenderCellParams,
} from '@mui/x-data-grid';
import localforage from 'localforage';
import BasePaper from '@/components/ui/BasePaper';
import BaseCard from '@/components/ui/BaseCard';
import BaseTitle from '@/components/ui/BaseTitle';
import { Create, Delete } from '@mui/icons-material';
import BaseButton from '@/components/ui/BaseButton';
import BaseDialog from '@/components/ui/BaseDialog';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { AdminRolePageState, AdminRolePageAction, BaseRole } from '@/types/pages/admin/role';
import { getRoleListAPI, deleteRoleAPI } from '@/api/admin/rbac';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '@/provider/contexts';
import { useTranslation } from 'react-i18next';
import BaseDataTable from '@/components/ui/BaseDataTable';
import BaseTableColumn from '@/components/ui/BaseTableColumn';

import '@/styles/admin.scss';
import SearchBar from '@/components/form/SearchBar';

export type apiRefType = React.MutableRefObject<GridApi>;

function reducer(state: AdminRolePageState, action: AdminRolePageAction) {
    switch (action.type) {
        case 'SET_TABLE_LOADING':
            return { ...state, tableLoading: action.payload };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        case 'SET_DATA_TABLE':
            return {
                ...state,
                tableLoading: false,
                dataTableList: action.payload,
            };
        default:
            return state;
    }
}

function AdminRolePage() {
    const { t } = useTranslation('common');
    const [search, setSearch] = useState('');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const { setNotification } = useAppContext();

    const nav = useNavigate();

    const defaultState: AdminRolePageState = {
        tableLoading: false,
        dataTableList: [],
        paginationModel: {
            page: 0,
            pageSize: 5,
        },
    };

    const [{ tableLoading, dataTableList, paginationModel }, dispatch] = useReducer(reducer, defaultState);

    const getRowId = (row: BaseRole) => {
        return row.role_id;
    };

    const fetchListHandler = async () => {
        dispatch({ type: 'SET_TABLE_LOADING', payload: true });
        try {
            const corporationId = (await localforage.getItem('corporation_id')) as number;

            const {
                data: { data, message, isSuccess },
            } = await getRoleListAPI(corporationId);

            const availableRoles = Array.isArray(data)
                ? []
                : Array.isArray(data?.available_roles)
                  ? data.available_roles
                  : [];

            if (isSuccess === false && message) {
                setNotification({
                    message,
                    severity: 'error',
                });
            }

            dispatch({ type: 'SET_DATA_TABLE', payload: availableRoles });
        } catch (error) {
            console.error('Failed to fetch roles:', error);
            setNotification({
                message: t('Failed to fetch roles.'),
                severity: 'error',
            });
            dispatch({ type: 'SET_DATA_TABLE', payload: [] });
        }
    };

    const handleClickSearch = (query: string) => {
        apiRef.current.setQuickFilterValues([query]);
    };

    const renderSimpleCell = useCallback((params: GridRenderCellParams) => {
        const value = params.value;
        const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
        return <BaseTableColumn columnValue={displayValue} />;
    }, []);

    const editItem = useCallback(
        (id: GridRowId) => {
            nav('/admin/role/' + id);
        },
        [nav],
    );

    const deleteItem = useCallback((id: GridRowId) => {
        setDeleteId(id as number);
        setIsDeleteDialogOpen(true);
    }, []);

    const columns: GridColDef[] = useMemo(
        () => [
            { field: 'role_name', headerName: t('Role'), flex: 1, minWidth: 140, renderCell: renderSimpleCell },
            {
                field: 'role_description',
                headerName: t('Description'),
                flex: 1,
                minWidth: 140,
                renderCell: renderSimpleCell,
            },
            {
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                flex: 1,
                minWidth: 90,
                hideable: false,
                disableColumnMenu: false,
                getActions: ({ id }) => {
                    return [
                        <GridActionsCellItem
                            icon={<Create />}
                            label={t('Edit')}
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={() => editItem(id)}
                        />,
                        <GridActionsCellItem
                            icon={<Delete />}
                            label={t('Delete')}
                            className="textPrimary"
                            onClick={() => deleteItem(id)}
                            color="inherit"
                        />,
                    ];
                },
            },
        ],
        [deleteItem, editItem, renderSimpleCell, t],
    );

    useEffect(() => {
        fetchListHandler();
    }, []);

    const apiRef = useGridApiRef();

    const handleConfirmDelete = async () => {
        try {
            const corporationId = (await localforage.getItem('corporation_id')) as number;
            const rs = await deleteRoleAPI(corporationId, deleteId as number);
            if (rs.data?.status == 200) {
                setNotification({
                    message: t('Delete successful!'),
                    severity: 'success',
                    // message: 'Delete Store is not available now',
                    // severity: 'error',
                });
                fetchListHandler();
            } else {
                setNotification({
                    message: t('Delete failed!'),
                    severity: 'error',
                });
            }
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            setNotification({
                message: t('An unexpected error occurred'),
                severity: 'error',
            });
        }

        setIsDeleteDialogOpen(false);
    };

    return (
        <>
            <BasePaper className={'admin-paper'}>
                <BaseTitle icon={<AdminPanelSettingsIcon />} title={t('Administration of Role')} />

                <BaseCard>
                    <div className="card-header">
                        <SearchBar
                            value={search}
                            onSearch={(value) => {
                                setSearch(value);
                                handleClickSearch(value);
                            }}
                            sx={{ my: 2 }}
                        />
                        <Link to="/admin/role/new">
                            <BaseButton startIcon={<Create />}>{t('NEW ROLE')}</BaseButton>
                        </Link>
                    </div>
                    <BaseDataTable
                        rows={dataTableList}
                        columns={columns}
                        getRowId={getRowId}
                        loading={tableLoading}
                        apiRef={apiRef}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(model) => {
                            dispatch({ type: 'SET_PAGINATION_MODEL', payload: model });
                        }}
                    />

                    <BaseDialog
                        open={isDeleteDialogOpen}
                        maxWidth="xs"
                        dialogTitle={t('Delete Role')}
                        confirmButtonText={t('Confirm')}
                        cancelButtonText={t('Cancel')}
                        onConfirm={handleConfirmDelete}
                        onClose={() => setIsDeleteDialogOpen(false)}
                    >
                        <div>{t('Confirm to delete?')}</div>
                    </BaseDialog>
                </BaseCard>
            </BasePaper>
        </>
    );
}

export default AdminRolePage;
